import React, { useState, useContext, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import "@fortawesome/fontawesome-free/css/all.min.css";
import styles from "../styles/Header.module.css";
import WorkflowIcon from "@mui/icons-material/AccountTree";
import { AuthContext } from "../AuthProvider";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSignOutAlt, faCog, faUser, faTasks } from "@fortawesome/free-solid-svg-icons";

const Header = ({ onSearch }) => {
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { keycloak, authenticated } = useContext(AuthContext);
  const [menuOpen, setMenuOpen] = useState(false);
  const [isUserAdmin, setIsUserAdmin] = useState(false);
  const menuRef = useRef(null);
  const inputRef = useRef(null);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const toggleUserMenu = () => {
    setMenuOpen(!menuOpen);
  };

  const onLogout = () => {
    keycloak.logout({ redirectUri: window.location.origin });
  };

  // Extract initials from user name
  const getUserInitials = () => {
    const user = keycloak?.tokenParsed;
    if (!user) return "U"; // Default Initial
    const firstName = user.given_name || "";
    const lastName = user.family_name || "";

    if (!firstName && !lastName) return "U";

    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
  };

  const getUserId = () => {
    const user = keycloak?.tokenParsed;
    if (!user) return null;
    return user.sub;
  };

  // Check if user is admin
  useEffect(() => {
    if (authenticated && keycloak?.tokenParsed) {
      const roles = keycloak.tokenParsed.realm_access?.roles || [];
      setIsUserAdmin(roles.includes("admin"));
    } else {
      setIsUserAdmin(false);
    }
  }, [authenticated, keycloak]);

  // Close menu when performing something else in the page
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setMenuOpen(false);
      }
    };

    const handleBlur = () => {
      setMenuOpen(false);
    };

    const handleResize = () => {
      setMenuOpen(false);
    };

    // Event Listeners
    document.addEventListener("mousedown", handleClickOutside);
    window.addEventListener("blur", handleBlur); // Detects tab changes
    window.addEventListener("resize", handleResize); // Detects zooming

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      window.removeEventListener("blur", handleBlur);
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      const query = e.target.value.trim();
      if (query) {
        inputRef.current.value = "";
        navigate(`/search?q=${query}`);
      }
    }
  };

  return (
    // <header className={"header bg-white shadow-md p-4"}>
    <header className={`${styles.header} shadow-md p-4`}>
      <div className={styles.container}>
        <div className={styles.headerContent}>
          {/* Left Section */}
          <div className={styles.leftSection}>
            <Link to="/" className={styles.headerLink}>
              <img
                src="/logo1.svg"
                alt="Waldiez Logo"
                className={styles.logo}
              />
              <p className={styles.headerText}>Waldiez</p>
            </Link>
            <div className={styles.searchContainer}>
              <i className={`fas fa-search ${styles.searchIcon}`}></i>
              <input
                type="text"
                className={styles.searchBar}
                placeholder="Search workflows, users..."
                onChange={(e) => onSearch(e.target.value)}
                onKeyDown={handleKeyDown}
                ref={inputRef}
              />
            </div>
          </div>

          {/* Right Section */}
          <div
            className={`${styles.rightSection} ${
              isMenuOpen ? styles.open : ""
            }`}
          >
            <Link to="/workflows" className={styles.headerLink}>
              <WorkflowIcon style={{ fontSize: "20px", marginRight: "8px" }} />
              Workflows
            </Link>
            <span className={styles.divider}></span>
            {process.env.REACT_APP_INCLUDE_STUDIO === "true" && authenticated && (
              <Link to="/studio" className={styles.headerLink}>
                <i className="fas fa-code" style={{ fontSize: "20px", marginRight: "8px" }}></i>
                Studio
              </Link>
            )}
            {process.env.REACT_APP_INCLUDE_STUDIO === "true" && authenticated && (
              <span className={styles.divider}></span>
            )}
            <div className={styles.authLinks}>
              {!authenticated && (
                <Link
                  className={styles.headerLink}
                  onClick={() => keycloak.login()}
                >
                  Login
                </Link>
              )}
              {authenticated && (
                <div className={styles.userMenu} ref={menuRef}>
                  {/* Avatar Icon */}
                  <div className={styles.avatar} onClick={toggleUserMenu}>
                    {getUserInitials()}
                  </div>
                  {/* Dropdown Menu */}
                  {menuOpen && (
                    <div className={styles.dropdownMenu}>
                      <button
                        className={styles.menuItem}
                        onClick={() => {
                          toggleUserMenu();
                          navigate(`/settings/account`);
                        }}
                      >
                        <FontAwesomeIcon icon={faCog} className={styles.icon} />
                        Account
                      </button>
                      <button
                        className={styles.menuItem}
                        onClick={() => {
                          toggleUserMenu();
                          navigate(`/u/${getUserId()}`);
                        }}
                      >
                        <FontAwesomeIcon
                          icon={faUser}
                          className={styles.icon}
                        />
                        Profile
                      </button>
                      {isUserAdmin && (
                        <button
                          className={styles.menuItem}
                          onClick={() => {
                            toggleUserMenu();
                            navigate(`/tasks`);
                          }}
                        >
                          <FontAwesomeIcon
                            icon={faTasks}
                            className={styles.icon}
                          />
                          Runner Tasks
                        </button>
                      )}
                      {isUserAdmin && process.env.REACT_APP_INCLUDE_SA_TASKS === "true" && (
                        <button
                          className={styles.menuItem}
                          onClick={() => {
                            toggleUserMenu();
                            navigate(`/admin/sa-tasks`);
                          }}
                        >
                          <FontAwesomeIcon
                            icon={faTasks}
                            className={styles.icon}
                          />
                          SA Tasks
                        </button>
                      )}
                      <button className={styles.menuItem} onClick={onLogout}>
                        <FontAwesomeIcon
                          icon={faSignOutAlt}
                          className={styles.icon}
                        />
                        Log Out
                      </button>
                    </div>
                  )}
                </div>
              )}
              {!authenticated && (
                <Link
                  className={`${styles.headerLink} ${styles.signUp}`}
                  onClick={() => keycloak.register()}
                >
                  Sign Up
                </Link>
              )}
            </div>
          </div>

          {/* Menu Toggle Button */}
          <button className={styles.menuToggle} onClick={toggleMenu}>
            <i className={`fas ${isMenuOpen ? "fa-xmark" : "fa-bars"}`}></i>
          </button>
        </div>
      </div>
    </header>
  );
};

export default Header;
